import { useState } from 'react';
import { Html } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { AlertTriangle, X, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

interface AlertsPanelProps {
  position: [number, number, number];
  alerts: Array<{
    id: string;
    deviceId: string;
    deviceName: string;
    sensorId?: string;
    sensorName?: string;
    sensorType?: string;
    severity: string;
    message: string;
    status: string;
    value?: number;
    unit?: string;
    timestamp: string;
    originalDeviceId?: string; // Added for navigation
  }>;
  sensorAlerts: Array<{
    id: string;
    name: string;
    type: string;
    value: number;
    unit: string;
    status?: 'normal' | 'warning' | 'critical';
    deviceId?: string; // Added for navigation
  }>;
  onClose: () => void;
  onViewDetails?: (deviceId: string) => void; // Added callback for navigation
  persistent?: boolean;
}

export function AlertsPanel({
  position,
  alerts,
  sensorAlerts,
  onClose,
  onViewDetails,
  persistent = false,
}: AlertsPanelProps) {
  const [pulseValue, setPulseValue] = useState(0);
  const [expanded, setExpanded] = useState(false);

  // Animation for pulsating effect
  useFrame((_, delta) => {
    setPulseValue((prev) => (prev + delta) % (Math.PI * 2));
  });

  // Determine severity level (highest)
  const hasCritical =
    alerts.some((a) => a.severity === 'critical') || sensorAlerts.some((s) => s.status === 'critical');

  const totalAlertCount = alerts.length + sensorAlerts.length;

  // Format timestamp
  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  // Handle navigation to device details page
  const handleViewDetails = (deviceId: string) => {
    if (onViewDetails && deviceId) {
      onViewDetails(deviceId);
    }
  };

  return (
    <Html
      position={position}
      center
      transform={!persistent}
      occlude={!persistent}
      distanceFactor={10}
      zIndexRange={[100, 0]}
    >
      <div
        className={`bg-background/90 backdrop-blur-md border p-4 rounded-lg shadow-lg w-[300px] ${hasCritical ? 'border-red-500' : 'border-amber-500'}`}
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-2">
            <AlertTriangle
              className={`h-4 w-4 ${hasCritical ? 'text-red-500' : 'text-amber-500'} ${Math.sin(pulseValue) > 0 ? 'opacity-100' : 'opacity-70'}`}
            />
            <h3 className="text-base font-bold flex items-center gap-1">
              Alerts
              <span
                className={`px-1.5 py-0.5 text-xs rounded-full ${hasCritical ? 'bg-red-500/20 text-red-500' : 'bg-amber-500/20 text-amber-500'}`}
              >
                {totalAlertCount}
              </span>
            </h3>
          </div>
          <div className="flex gap-1">
            <button
              className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <button
              className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!expanded ? (
          <div className="text-xs text-muted-foreground mb-2">
            {hasCritical ? (
              <span className="text-red-500 font-medium">Critical alerts detected</span>
            ) : (
              <span className="text-amber-500 font-medium">Warning alerts detected</span>
            )}
          </div>
        ) : (
          <>
            <div className="max-h-[240px] overflow-y-auto pr-1 space-y-2 mb-2">
              {/* API Alerts */}
              {alerts.length > 0
                ? alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`p-2 text-xs rounded ${
                        alert.severity === 'critical'
                          ? 'bg-red-500/10 border-red-500/40 border'
                          : 'bg-amber-500/10 border-amber-500/40 border'
                      }`}
                    >
                      <div className="flex justify-between">
                        <div className="font-medium">{alert.sensorName || alert.deviceName}</div>
                        <div className="text-muted-foreground">{formatTime(alert.timestamp)}</div>
                      </div>
                      <div className="mt-1">{alert.message}</div>
                      {alert.value !== undefined && (
                        <div className="mt-1 font-medium">
                          Value: {alert.value.toFixed(1)} {alert.unit}
                        </div>
                      )}
                      {alert.originalDeviceId && onViewDetails && (
                        <button
                          onClick={() => handleViewDetails(alert.originalDeviceId || '')}
                          className="mt-2 flex items-center gap-1 text-xs p-1 bg-primary/10 text-primary hover:bg-primary/20 rounded"
                        >
                          <ExternalLink size={12} />
                          View in Alerts Tab
                        </button>
                      )}
                    </div>
                  ))
                : null}

              {/* Sensor Status Alerts */}
              {sensorAlerts.length > 0
                ? sensorAlerts.map((sensor) => (
                    <div
                      key={sensor.id}
                      className={`p-2 text-xs rounded ${
                        sensor.status === 'critical'
                          ? 'bg-red-500/10 border-red-500/40 border'
                          : 'bg-amber-500/10 border-amber-500/40 border'
                      }`}
                    >
                      <div className="flex justify-between">
                        <div className="font-medium">{sensor.name}</div>
                        <div className={sensor.status === 'critical' ? 'text-red-500' : 'text-amber-500'}>
                          {sensor.status?.toUpperCase()}
                        </div>
                      </div>
                      <div className="mt-1">Abnormal {sensor.type} reading detected</div>
                      <div className="mt-1 font-medium">
                        Value: {sensor.value.toFixed(1)} {sensor.unit}
                      </div>
                      {sensor.deviceId && onViewDetails && (
                        <button
                          onClick={() => handleViewDetails(sensor.deviceId || '')}
                          className="mt-2 flex items-center gap-1 text-xs p-1 bg-primary/10 text-primary hover:bg-primary/20 rounded"
                        >
                          <ExternalLink size={12} />
                          View in Alerts Tab
                        </button>
                      )}
                    </div>
                  ))
                : null}

              {totalAlertCount === 0 && (
                <div className="text-center py-4 text-muted-foreground text-sm">No active alerts</div>
              )}
            </div>

            <div className="pt-2 border-t border-muted text-[10px] text-center text-muted-foreground">
              Click alert to view details
            </div>
          </>
        )}
      </div>
    </Html>
  );
}
